<template>
  <main>
    <CdrContainer
      class="breadcrumbs"
      tag="section"
    >
      <BreadcrumbsComponent :breadcrumbs="computedBreadcrumbs" />
    </CdrContainer>
    <SessionHeroComponent :base-image-uri="baseImageUri" />
    <CdrContainer
      class="section--event-info-price-register section--divider"
      tag="section"
    >
      <SessionHeadingInfoComponent v-bind="headingInfoConfig" />
      <SessionPriceRegisterComponent v-bind="pricingInfoProps" />
    </CdrContainer>
    <CdrContainer tag="section">
      <SessionOverviewComponent
        :activity-level="activityLevel"
        :all-activity-levels="activityLevelsContent"
        :all-skill-levels="skillLevelsContent"
        :base-image-uri="baseImageUri"
        :brief-description="course.briefDescription"
        :course-attributes="courseAttributes"
        :course-gear-info="course.courseGearInfo"
        :course-name="course.name"
        :device-size="deviceSize"
        :display-location-name="displayLocation.name"
        :display-legal-forms="displayLegalForms"
        :outcomes="course.outcomes"
        :long-description="course.longDescription"
        :session-data="sessionData"
        :skill-level="skillLevel"
        data-ui="top-of-overview"
      />
    </CdrContainer>
    <CdrContainer tag="section">
      <div class="session-location-cancel-container">
        <SessionLocationDetailsComponent v-bind="locationDetailsProps" />
        <SessionMoreQuestionsComponent
          :operator-email="sessionData.operatorEmail"
          :operator-phone-number="sessionData.operatorPhoneNumber"
          :from-trek="sessionData.fromTrek"
        />
        <SessionCancellationPolicyComponent
          v-if="showCancellationPolicy"
          :start-date="sessionData.start"
        />
      </div>
    </CdrContainer>
  </main>
</template>

<script>
import { CdrContainer } from '@rei/cedar';
import { win } from '@rei/browser-shim';
import BreadcrumbsComponent from '../../components/BreadcrumbsComponent.vue';
import SessionCancellationPolicyComponent from '../../components/SessionCancellationPolicyComponent.vue';
import SessionHeadingInfoComponent from '../../components/SessionHeadingInfoComponent.vue';
import SessionHeroComponent from '../../components/SessionHeroComponent.vue';
import SessionLocationDetailsComponent from '../../components/SessionLocationDetailsComponent.vue';
import SessionMoreQuestionsComponent from '../../components/SessionMoreQuestionsComponent.vue';
import SessionOverviewComponent from '../../components/SessionOverviewComponent.vue';
import SessionPriceRegisterComponent from '../../components/SessionPriceRegisterComponent.vue';
import getLevel from '../../mixins/getLevel';

export default {
  name: 'SessionDetailsPageComponent',
  components: {
    CdrContainer,
    BreadcrumbsComponent,
    SessionCancellationPolicyComponent,
    SessionHeadingInfoComponent,
    SessionHeroComponent,
    SessionLocationDetailsComponent,
    SessionMoreQuestionsComponent,
    SessionOverviewComponent,
    SessionPriceRegisterComponent,
  },
  mixins: [getLevel],
  props: {
    activityLevels: {
      type: Array,
      required: true,
    },
    allActivityLevels: {
      type: Object,
      required: true,
    },
    allSkillLevels: {
      type: Object,
      required: true,
    },
    baseImageUri: {
      type: String,
      required: true,
    },
    breadcrumbs: {
      type: Object,
      required: true,
    },
    course: {
      type: Object,
      required: true,
    },
    displayLegalForms: {
      type: Array,
      required: true,
    },
    displayLocation: {
      type: Object,
      required: true,
    },
    displayLocationPageUrl: {
      type: String,
      required: true,
    },
    displayMeetingLocation: {
      type: Object,
      required: true,
    },
    displayMeetingLocationPageUrl: {
      type: String,
      required: true,
    },
    locationGoogleMapsUrl: {
      type: String,
      required: true,
    },
    otherSessions: {
      type: Boolean,
      required: true,
    },
    pageTitle: {
      type: String,
      required: true,
    },
    relCanonicalUrl: {
      type: String,
      required: true,
    },
    sessionData: {
      type: Object,
      required: true,
    },
    skillLevels: {
      type: Array,
      required: true,
    },
    dayWaitlistEnabled: {
      type: Boolean,
      required: true,
    },
    dayWaitlistUri: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    deviceSize: 'mobile',
    screenWidth: 375,
  }),
  computed: {
    activityLevel() {
      const { activityLevels: inputArr } = this;
      return this.getLevel({ type: 'activity-level', inputArr });
    },
    activityLevelsContent() {
      return this.pruneAttributes(this.allActivityLevels);
    },
    computedBreadcrumbs() {
      const { breadcrumbs = {} } = this;
      return Object.values(breadcrumbs).map(({ data: url, name }) => ({ item: { url, name } }));
    },
    courseAttributes() {
      const minAge = this.course.minimumAge;
      const groupSize = this.sessionData.spots.capacity;
      let instructorName;
      this.sessionData.instructors.forEach((instructor) => {
        instructorName = instructor.name;
      });
      const { activityLevel, skillLevel } = this;
      const courseAttributes = [];

      if (activityLevel) courseAttributes.push(activityLevel);
      if (skillLevel) courseAttributes.push(skillLevel);
      if (minAge) {
        const heading = 'Minimum age';
        const subheading = minAge;
        courseAttributes.push({
          type: 'min-age',
          heading,
          subheading,
          tip: false,
        });
      }
      if (groupSize) {
        const heading = 'Group size';
        const subheading = `${groupSize} people`;
        courseAttributes.push({
          type: 'group-size',
          heading,
          subheading,
          tip: false,
        });
      }
      if (instructorName && instructorName !== 'REI Outdoor School Instructor') {
        const heading = 'Instructor';
        const subheading = instructorName;
        courseAttributes.push({
          type: 'instructor',
          heading,
          subheading,
          tip: false,
        });
      }

      return courseAttributes;
    },
    gpsLat() {
      return this.displayLocation.gpsLat;
    },
    gpsLong() {
      return this.displayLocation.gpsLong;
    },
    headingInfoConfig() {
      const { displayLocation, locationIsStore, storeUrl } = this;

      // Location name config
      // previous logic allowed location not to appear, this preserves that
      let locationLinkProps = false;
      const { name: locationName = '' } = displayLocation;
      if (locationIsStore && storeUrl !== null) {
        locationLinkProps = { href: storeUrl, text: locationName, type: 'url' };
      }
      if (!locationIsStore) {
        locationLinkProps = { href: '#location-details', text: locationName, type: 'id' };
      }

      return {
        'data-is-private-event': this.isPrivateEvent,
        'data-sponsor-store-number': this.getSponsorStoreNumber,
        deviceSize: this.deviceSize,
        locationLinkProps,
        locationIsStore: this.locationIsStore,
        meetAtProps: this.meetAtProps,
        moreDatesUrl: this.moreDatesUrl,
        otherSessions: this.otherSessions,
        pageTitle: this.course.name,
        sessionData: this.sessionData,
      };
    },
    isOpen() {
      return this.sessionData.status === 'OPEN';
    },
    isPrivateEvent() {
      const { sessionData } = this;
      // ensure a true boolean value is passed
      return (!!sessionData.private).toString();
    },
    getSponsorStoreNumber() {
      const { sessionData } = this;
      let storeNumberValue;
      if (sessionData.sponsorStore) {
        storeNumberValue = sessionData.sponsorStore.storeNumber;
      } else if (sessionData.location && sessionData.location.isStore) {
        storeNumberValue = sessionData.location.storeNumber;
      }
      return storeNumberValue;
    },
    isRegRequired() {
      return this.sessionData.registrationRequired === true;
    },
    isThirdParty() {
      return this.sessionData.spots.registrationLocation === 'THIRD_PARTY';
    },
    isWaitListed() {
      return this.sessionData.spots.status === 'WAIT_LIST';
    },
    locationDetailsProps() {
      return {
        displayLocation: this.displayLocation,
        displayMeetingLocation: this.displayMeetingLocation,
        gpsLat: this.gpsLat,
        gpsLong: this.gpsLong,
        locationIsStore: this.locationIsStore,
        locationGoogleMapsUrl: this.locationGoogleMapsUrl,
        meetAtProps: this.meetAtProps,
        meetAtStoreUrl: this.meetAtStoreUrl,
        meetAtLocationIsStore: this.meetAtLocationIsStore,
        meetAtMapDirectionsUrl: this.meetAtMapDirectionsUrl,
        showMeetAtLocation: this.showMeetAtLocation,
        storeUrl: this.storeUrl,
      };
    },
    locationIsStore() {
      return !!this.displayLocation.isStore;
    },
    locationName() {
      return this.displayLocation && this.displayLocation.name;
    },
    meetAtProps() {
      const { displayMeetingLocation, showMeetAtLocation } = this;

      // Meet at config
      let meetAtProps = false;
      if (showMeetAtLocation) {
        meetAtProps = { href: this.meetAtStoreUrl, text: displayMeetingLocation.name };
      }
      return meetAtProps;
    },
    /*
      formerly meetingStoreUrl
    */
    meetAtStoreUrl() {
      const { displayMeetingLocation } = this;
      const { storePageUrl: url = '' } = displayMeetingLocation;
      // if the storePageUrl exists, return displayMeetingLocationPageUrl (which is the complete url)
      if (!url) return false;
      return url.startsWith('/') ? url : `/${url}`;
    },
    meetingGpsLat() {
      const { displayMeetingLocation } = this;
      return displayMeetingLocation && displayMeetingLocation.gpsLat;
    },
    meetingGpsLong() {
      const { displayMeetingLocation } = this;
      return displayMeetingLocation && displayMeetingLocation.gpsLong;
    },
    /*
      formerly meetingLocationIsStore
    */
    meetAtLocationIsStore() {
      const { displayMeetingLocation } = this;
      return !!(displayMeetingLocation && displayMeetingLocation.isStore);
    },
    meetAtMapDirectionsUrl() {
      const { displayMeetingLocation, meetingGpsLat, meetingGpsLong } = this;
      let str = '';
      if (displayMeetingLocation) {
        str = `https://maps.google.com?daddr=${meetingGpsLat},${meetingGpsLong}&zoom=13&size=400x400&sensor=false`;
      }
      return str;
    },
    meetingStoreUrl() {
      const { displayMeetingLocation, storeUrl } = this;
      return displayMeetingLocation !== null && storeUrl ? `/${storeUrl}` : '#';
    },
    moreDatesUrl() {
      const {
        computedBreadcrumbs,
        sessionData: { uri },
      } = this;
      const idOnCoursePage = '#sessions-by-month';
      let returnValue = this.relCanonicalUrl;
      const lastIndex = computedBreadcrumbs.length - 1;
      // use last breadcrumb if breadcrumbs are present
      if (lastIndex > -1) {
        const { item = {} } = computedBreadcrumbs[lastIndex] || {};
        ({ url: returnValue = '' } = item);
      } else if (uri && uri.match) {
        // matches "/events/<any-digit-combo>/<any-alphanumeric-combo>"
        [returnValue] = uri.match(/\/events\/([\d]*)\/([a-zA-Z0-9-]*)/g) || [];
      }
      return `${returnValue}${idOnCoursePage}`;
    },
    pricingInfoProps() {
      const { displayLocation, sessionData, dayWaitlistEnabled, dayWaitlistUri } = this;
      const {
        end,
        memberPrice,
        nonMemberPrice,
        partners,
        registrationRequired,
        spots,
        status,
        start,
        sessionId,
        fromTrek,
        operatorEmail,
        operatorPhoneNumber,
      } = sessionData;
      const { name: loc } = displayLocation;
      return {
        deviceSize: this.deviceSize,
        endDate: end,
        gpsLat: this.gpsLat,
        gpsLong: this.gpsLong,
        loc,
        memberPrice,
        moreDatesUrl: this.moreDatesUrl,
        nonMemberPrice,
        otherSessions: this.otherSessions,
        pageTitle: this.course.name,
        partners,
        registrationRequired,
        sessionId,
        startDate: start,
        spots,
        status,
        fromTrek,
        operatorEmail,
        operatorPhoneNumber,
        dayWaitlistEnabled,
        dayWaitlistUri,
      };
    },
    showCancellationPolicy() {
      return this.isOpen && !this.isThirdParty && !this.isWaitListed && this.isRegRequired;
    },
    skillLevel() {
      const { skillLevels: inputArr } = this;
      return this.getLevel({ type: 'skill-level', inputArr });
    },
    skillLevelsContent() {
      return this.pruneAttributes(this.allSkillLevels);
    },
    storeUrl() {
      const { displayLocation } = this;
      if (displayLocation && displayLocation.storePageUrl) {
        return `/${this.displayLocation.storePageUrl}`;
      }
      return false;
    },
    /*
      returns true only if displayMeetingLocation object exists and contains non-null values
    */
    showMeetAtLocation() {
      return !!(
        this.displayMeetingLocation &&
        this.displayMeetingLocation.name &&
        this.displayMeetingLocation.name !== this.locationName
      );
    },
    style() {
      const { baseImageUri } = this;
      return { backgroundImage: `url(${baseImageUri})` };
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.onResize();
      // eslint-disable-next-line
      win.addEventListener && win.addEventListener('resize', this.onResize);
    });
  },
  beforeUnmount() {
    // eslint-disable-next-line
    win.removeEventListener && win.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      const withinMediaQuery = win.matchMedia('(max-width: 767px)').matches;
      this.deviceSize = withinMediaQuery ? 'mobile' : 'desktop';
      this.screenWidth = document.body.clientWidth;
    },
    pruneAttributes(obj) {
      return Object.keys(obj)
        .map((id) => ({ id, ...obj[id] }))
        .filter(({ level }) => level > 0)
        .sort((a, b) => (a.level > b.level ? 1 : -1), {});
    },
  },
};
</script>
